/**
 * @Page ResiGrp
 * @Description 상주원 등록(RST02) > 품목그룹코드 배정 팝업
 *
 * 동작 방식:
 *  1. 팝업 열릴 때 /rst02/resi/grpsearch로 recd에 이미 배정된 grpt 목록을 로드
 *  2. /rst03/wagr/search에서 WRTWAGR 품목그룹 마스터를 조회 후 기존 배정 행을 자동 체크
 *  3. 확인 클릭 시:
 *     - 현재 체크 O & 기존 X → /rst02/resi/grpinsert
 *     - getRowAllData() 행 중, 기존 O & 현재 체크 X → /rst02/resi/grpdelete
 *  4. 완료 후 재조회 + 팝업 내 체크 갱신 + 부모(RST02) 우측 그리드 재조회
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import * as Common from '@/utils/Common';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;

function ResiGrp({ onClose, recd, vnt1, onRefresh }) {

    const token = Common.getToken();

    const grdResiGrp = useRef();

    // Alert
    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    // 기존 배정 Set - 키: grpt
    const [existingSet, setExistingSet] = useState(new Set());
    const latestExistingSet = useRef(new Set());
    const pendingPreSelect = useRef(false);

    // 업체코드
    const idata = {
        kVnt1: vnt1 || Common.getToken("vand"),
    };

    let [searchApi, setSearchApi] = useState(false);

    // searchApi 조회 완료 후 기존 배정 행 자동 체크
    const onClearApi = useCallback(() => {
        setSearchApi(false);
        if (pendingPreSelect.current) {
            pendingPreSelect.current = false;
            setTimeout(() => {
                const snap = latestExistingSet.current;
                if (grdResiGrp.current && grdResiGrp.current.selectRowsByCondition) {
                    grdResiGrp.current.selectRowsByCondition(
                        data => snap.has(data.grtp)
                    );
                }
            }, 300);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            if (recd) {
                await loadExistingResiGrp();
            }
            // 기존 배정 로드 완료 후 WRTWAGR 품목그룹 조회 + 체크 표시
            pendingPreSelect.current = true;
            const para = `{"gryn":"Y"}`;
            const query = SERVER_URL + "/rst03/wagr/search?query=" + encodeURIComponent(para);
            setSearchApi(query);
        };
        init();
    }, []);

    // WRTRESI 기존 배정 조회
    const loadExistingResiGrp = async () => {
        const para = `{"kVnt1":"${vnt1}","recd":"${recd}","vand":"${vnt1}"}`;
        const query = SERVER_URL + "/rst02/resi/grpsearch?query=" + encodeURI(para);
        let resultSet = new Set();
        await fetch(query, { headers: { Authorization: token } })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.data) {
                    const keys = data.data.map(item => item.grpt).filter(Boolean);
                    resultSet = new Set(keys);
                    setExistingSet(resultSet);
                    latestExistingSet.current = resultSet;
                }
            })
            .catch(() => { });
        return resultSet;
    };

    // WRTWAGR 품목그룹 마스터 조회
    const onSearch = () => {
        const para = `{"gryn":"Y"}`;
        const query = SERVER_URL + "/rst03/wagr/search?query=" + encodeURIComponent(para);
        pendingPreSelect.current = true;
        setSearchApi(query);
    };

    // 확인: INSERT(새 체크) + DELETE(기존 해제)
    const onConfirm = async () => {
        const selectedNodes = grdResiGrp.current?.getSelectedNodes() || [];
        const currentSet = new Set(selectedNodes.map(n => n.data.grtp));

        const currentExistingSet = latestExistingSet.current;
        // INSERT: 체크 O & 기존 X
        const toInsert = selectedNodes
            .filter(n => !currentExistingSet.has(n.data.grtp))
            .map(n => ({ vand: idata.kVnt1, recd, grpt: n.data.grtp }));

        // DELETE: 그리드 표시 행 중 기존 O & 체크 X (getRowAllData 기준 → 창고 필터 변경 시 버그 없음)
        const allGridRows = grdResiGrp.current?.getRowAllData() || [];
        const toDelete = allGridRows
            .filter(row => currentExistingSet.has(row.grtp) && !currentSet.has(row.grtp))
            .map(row => ({ vand: idata.kVnt1, recd, grpt: row.grtp }));

        if (toInsert.length === 0 && toDelete.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "변경된 항목이 없습니다." });
            return;
        }

        let hasError = false;
        const errors = [];

        if (toInsert.length > 0) {
            await fetch(SERVER_URL + "/rst02/resi/grpinsert", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: token },
                body: JSON.stringify(toInsert),
            })
                .then(r => r.json())
                .then(data => { if (!data.success) { hasError = true; errors.push(data.errors?.message || "배정 등록 오류"); } })
                .catch(() => { hasError = true; errors.push("배정 등록 서버 오류"); });
        }

        if (toDelete.length > 0) {
            await fetch(SERVER_URL + "/rst02/resi/grpdelete", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: token },
                body: JSON.stringify(toDelete),
            })
                .then(r => r.json())
                .then(data => { if (!data.success) { hasError = true; errors.push(data.errors?.message || "배정 삭제 오류"); } })
                .catch(() => { hasError = true; errors.push("배정 삭제 서버 오류"); });
        }

        if (hasError) {
            setMessage({ openAlert: true, severity: "error", msg: errors.join(" / ") });
            return;
        }

        setMessage({ openAlert: true, severity: "success", msg: `완료: 배정 ${toInsert.length}건, 해제 ${toDelete.length}건` });

        // 재조회 + 체크 갱신
        const refreshedSet = await loadExistingResiGrp();
        setTimeout(() => {
            if (grdResiGrp.current && grdResiGrp.current.selectRowsByCondition) {
                grdResiGrp.current.selectRowsByCondition(
                    data => refreshedSet.has(data.grtp)
                );
            }
        }, 300);

        // 저장 완료 후 팝업을 닫으면 부모(RST02)가 우측 Grid를 재조회한다.
        onClose(true);
    };

    // 컬럼 정의
    const columnDefs = [
        {
            headerName: "품목그룹코드", field: "grtp",
            sortable: true, filter: true, flex: 1, minWidth: 110,
            editable: false, cellClass: ["nocheckbox", "alignL"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "품목그룹명", field: "grnm",
            sortable: true, filter: true, flex: 2, minWidth: 140,
            editable: false, cellClass: ["nocheckbox", "alignL"],
        },
    ];

    return (
        <>
            <CustomAlert
                open={openAlert} severity={severity} duration={3000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />

            {/* 헤더 */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif", fontSize: "14px" }}>
                    품목그룹 배정 - 상주원 [{recd}]
                </b>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <span style={{ fontSize: "11px", color: "#888" }}>(체크 = 배정, 해제 = 삭제)</span>
                    <button
                        onClick={() => onClose(false)}
                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                    >
                        <img src="/images/button/close_button.png" alt="close" />
                    </button>
                </div>
            </div>

            {/* 버튼 바 */}
            <div style={{
                display: "flex", flexFlow: "row nowrap", justifyContent: "flex-end",
                alignItems: "center", height: "50px",
                borderTop: "1px solid #BFBDCD", borderBottom: "1px solid #BFBDCD",
                background: "#fff", paddingRight: "5px",
            }}>
                <button
                    type="button" onClick={onSearch}
                    style={{
                        display: "flex", alignItems: "center", height: "30px",
                        border: "1px solid #B7BCD6", borderRadius: "2px", background: "#fff",
                        marginLeft: "10px", fontWeight: 600, cursor: "pointer",
                        padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_search.png" alt="Search" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>조회</span>
                </button>
                <button
                    type="button" onClick={onConfirm}
                    style={{
                        display: "flex", alignItems: "center", height: "30px",
                        border: "1px solid #B7BCD6", borderRadius: "2px", background: "#fff",
                        marginLeft: "10px", fontWeight: 600, cursor: "pointer",
                        padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_insert.png" alt="insert" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>입력</span>
                </button>
            </div>

            {/* 안내 */}
            <div style={{
                background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "4px",
                padding: "5px 10px", marginTop: "4px", marginBottom: "4px",
                fontSize: "12px", color: "#1565c0",
            }}>
                📣 이미 배정된 품목그룹은 <b>체크됨</b>으로 표시됩니다. 체크 변경 후 <b>✔ 입력</b>을 클릭하세요.
            </div>

            {/* 그리드 */}
            <div className="grid_type01">
                <div className="grid_wrap01">
                    <div className="grid_cont_box" style={{ height: "360px", width: "100%" }}>
                        <Grid
                            ref={grdResiGrp}
                            gridId={"RESIGRP"}
                            columnDefs={columnDefs}
                            pagination={false}
                            paginationCsv={false}
                            paginationPageSize={"1000"}
                            rowMultiSelectWithClick={false}
                            suppressRowClickSelection={true}
                            rowSelection={"multiple"}
                            enableRangeSelection={false}
                            enableCellTextSelection={true}
                            isFirst={true}
                            displayNo={false}
                            searchApi={searchApi}
                            onClearApi={onClearApi}
                            onCellValueChangedEvent={async () => { }}
                            useMessage={false}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default ResiGrp;
